import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  loadAcceptJs,
  tokenizeWithAcceptJs,
} from '#/shared/config/authorize-net'
import type { AuthnetConfig } from '#/shared/config/authorize-net'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import {
  ADD_CARD_TOAST_KEYS,
  useAddCardFields,
  useSaveCard,
} from './add-card-shared'
import type { AddCardFields, UseAddCardArgs } from './add-card-shared'

const ACCEPT_RESULT_OK = 'Ok'
const CVC_MIN_LENGTH = 3
const CARD_NUMBER_MIN_LENGTH = 13

export interface AuthnetCard {
  number: string
  expiry: string
  cvc: string
}

export interface UseAddCardAuthnetResult extends AddCardFields {
  card: AuthnetCard
  setCardField: (key: keyof AuthnetCard, value: string) => void
  cardError: string | undefined
  canSubmit: boolean
  isSubmitting: boolean
  submit: () => Promise<void>
}

interface ParsedExpiry {
  month: string
  year: string
  monthNum: number
  yearNum: number
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function parseExpiry(raw: string): ParsedExpiry | undefined {
  const digits = onlyDigits(raw)
  if (digits.length !== 4 && digits.length !== 6) return undefined
  const monthNum = Number(digits.slice(0, 2))
  if (monthNum < 1 || monthNum > 12) return undefined
  const yearDigits = digits.slice(2)
  const yearNum =
    yearDigits.length === 2 ? 2000 + Number(yearDigits) : Number(yearDigits)
  return {
    month: digits.slice(0, 2),
    year: String(yearNum).slice(-2),
    monthNum,
    yearNum,
  }
}

export function useAddCardAuthnet(
  args: UseAddCardArgs,
  authnet: AuthnetConfig,
): UseAddCardAuthnetResult {
  const { t } = useTranslation()
  const fields = useAddCardFields()
  const { save, isSaving } = useSaveCard()
  const [card, setCard] = useState<AuthnetCard>({
    number: '',
    expiry: '',
    cvc: '',
  })
  const [cardError, setCardError] = useState<string | undefined>(undefined)
  const [isTokenizing, setIsTokenizing] = useState(false)

  useEffect(() => {
    void loadAcceptJs(authnet.environment).catch(() => undefined)
  }, [authnet.environment])

  const setCardField = (key: keyof AuthnetCard, value: string) =>
    setCard((prev) => ({ ...prev, [key]: value }))

  const expiry = parseExpiry(card.expiry)
  const isCardValid =
    onlyDigits(card.number).length >= CARD_NUMBER_MIN_LENGTH &&
    expiry !== undefined &&
    onlyDigits(card.cvc).length >= CVC_MIN_LENGTH
  const isSubmitting = isTokenizing || isSaving
  const canSubmit = isCardValid && Boolean(args.customerId) && !isSubmitting

  const submit = async () => {
    if (!expiry || !canSubmit) return
    setIsTokenizing(true)
    setCardError(undefined)
    try {
      const response = await tokenizeWithAcceptJs(authnet, {
        cardNumber: onlyDigits(card.number),
        month: expiry.month,
        year: expiry.year,
        cardCode: onlyDigits(card.cvc),
      })
      if (response.messages.resultCode !== ACCEPT_RESULT_OK) {
        const message = response.messages.message.at(0)?.text
        setCardError(message)
        toast.error(message ?? t(ADD_CARD_TOAST_KEYS.error))
        return
      }
      await save(args, fields, {
        paymentToken: `${response.opaqueData.dataDescriptor}::${response.opaqueData.dataValue}`,
        expMonth: expiry.monthNum,
        expYear: expiry.yearNum,
      })
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? t(ADD_CARD_TOAST_KEYS.error))
    } finally {
      setIsTokenizing(false)
    }
  }

  return {
    ...fields,
    card,
    setCardField,
    cardError,
    canSubmit,
    isSubmitting,
    submit,
  }
}
